import type { GraphDTO, CommitDTO } from '@clearflow/shared';

export interface GitgraphCommitInput {
  hash: string;
  subject: string;
  body: string;
  author: { name: string; email: string; timestamp: number };
  parents: string[];
  refs: string[];
}

export interface GitgraphModel {
  commits: GitgraphCommitInput[];
  branchHeads: Record<string, string>; // branchName -> sha
  tags: Record<string, string>; // tagName -> sha
  // Consommé par computeLayout.ts (tie-break de lane) — une branche protégée hors trunk (ex.
  // main-next) est un second trunk de facto : diverger tôt et ne jamais se refermer.
  protectedBranches: Set<string>;
}

export function buildGitgraphModel(graph: GraphDTO, selectedBranches: Set<string>): GitgraphModel {
  const branchHeads = Object.fromEntries(
    graph.branches.filter((b) => selectedBranches.has(b.name)).map((b) => [b.name, b.headSha]),
  );
  const protectedBranches = new Set(
    graph.branches.filter((b) => selectedBranches.has(b.name) && b.isProtected).map((b) => b.name),
  );
  const reachable = new Set<string>();
  const queue: string[] = Object.values(branchHeads);
  const commitMap = new Map(graph.commits.map((c) => [c.sha, c]));
  while (queue.length) {
    const sha = queue.shift()!;
    if (reachable.has(sha)) continue;
    reachable.add(sha);
    const c = commitMap.get(sha);
    if (c) queue.push(...c.parents);
  }

  // Assign each commit a primary branch via first-parent traversal.
  // main/master always acts as the visual trunk (priority 0) so that projects
  // where the GitLab default branch is a release/* branch still render with main
  // on lane 0 and the release branch on its own separate lane.
  const branchByName = new Map(graph.branches.map((b) => [b.name, b]));
  const primaryBranch = new Map<string, string>();
  const sortedBranches = Object.entries(branchHeads).sort(([a], [b]) => {
    const priority = (name: string) => {
      if (name === 'main' || name === 'master')      return 0;
      if (name === graph.defaultBranch)              return 1;
      if (/(^|\/)release($|\/)/.test(name))          return 2;
      if (name === 'develop')                        return 2;
      // Une branche protégée (ex: main-next à côté de main) est un second trunk de fait —
      // sans ce cas, elle retombe au même tier 3 que n'importe quelle branche feature, et le
      // tri stable départage alors les ex-æquo par ordre alphabétique de l'API GitLab (pas par
      // pertinence). Une branche feature créée sur un commit déjà ancêtre de main-next, et dont
      // le nom la place avant elle alphabétiquement, "volait" alors toute la lignée partagée —
      // main-next se retrouvait réduite à son seul dernier commit, tout le reste (couleur y
      // compris) attribué à la branche feature (2026-09-11).
      if (branchByName.get(name)?.isProtected)       return 2;
      return 3;
    };
    return priority(a) - priority(b);
  });
  for (const [branchName, headSha] of sortedBranches) {
    let sha: string | undefined = headSha;
    while (sha && !primaryBranch.has(sha)) {
      primaryBranch.set(sha, branchName);
      sha = commitMap.get(sha)?.parents[0];
    }
  }

  const commits: GitgraphCommitInput[] = graph.commits
    .filter((c) => {
      if (!reachable.has(c.sha)) return false;
      const primary = primaryBranch.get(c.sha);
      // No primaryBranch means the commit is only reachable via a non-first-parent
      // merge from a deleted branch — exclude it.
      if (!primary) return false;
      return true;
    })
    .map((c: CommitDTO) => {
      const primary = primaryBranch.get(c.sha);
      const filteredRefs = c.refs.filter((r) => !r.startsWith('tag:') && selectedBranches.has(r));
      // Only trust refs that the first-parent traversal confirmed.
      // Commits reachable via non-first-parent merges carry stale refs from living
      // branches that merely contain them — those refs must not drive lane placement.
      const refs = primary && selectedBranches.has(primary)
        ? [primary, ...filteredRefs.filter((r) => r !== primary)]
        : [];
      return {
        hash: c.sha,
        subject: c.title,
        body: c.message,
        author: {
          name: c.authorName,
          email: c.authorEmail,
          timestamp: Date.parse(c.authoredDate) / 1000,
        },
        parents: c.parents,
        refs,
      };
    })
    .sort((a, b) => a.author.timestamp - b.author.timestamp);

  const tags = Object.fromEntries(graph.tags.map((t) => [t.name, t.sha]));

  return { commits, branchHeads, tags, protectedBranches };
}
